import {
  arrow,
  autoPlacement,
  computePosition,
  ComputePositionReturn,
  Elements,
  offset,
  shift,
  size,
} from '@floating-ui/dom';
import throttle from 'lodash/throttle';
import { MutableRefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { TooltipPosition as Position } from 'craft/resolvers/Hotspot/types';
import { useViewportDom } from 'craft/state';
import { getSelectorFromIframe } from 'craft/utils/snapshot-iframe';

const ALLOWED_PLACEMENTS = [Position.Top, Position.Bottom, Position.Left, Position.Right];

interface Props {
  tooltipPosition: Position;
  beaconRef: MutableRefObject<HTMLDivElement | undefined>;
  isActive: boolean;
  selector?: string;
}

export const useTooltipPosition = ({ tooltipPosition, beaconRef, isActive, selector }: Props) => {
  const wrapperRef = useRef<HTMLDivElement | undefined>();
  const arrowRef = useRef<HTMLDivElement | undefined>();
  const { viewportContentEl, viewportWrapperEl } = useViewportDom();

  const [floatingComputedPosition, setFoatingComputedPosition] = useState<ComputePositionReturn>(
    {} as ComputePositionReturn,
  );

  const calculateSetPosition = useCallback(async () => {
    if (!wrapperRef.current || !beaconRef.current || !arrowRef.current || !viewportContentEl) {
      return;
    }

    const hasAutoPlacement =
      tooltipPosition === Position.Auto
        ? [
            autoPlacement({
              // @ts-ignore
              allowedPlacements: ALLOWED_PLACEMENTS,
              padding: 16,
              boundary: viewportContentEl ?? undefined,
            }),
          ]
        : [];

    const hasManualPlacement =
      tooltipPosition !== Position.Auto ? { placement: tooltipPosition } : {};

    // We need to strip the maxWidth and maxHeight every time before recalculating these values
    // because they were persisting between different tooltip positions giving the wrong result
    // @ts-ignore
    wrapperRef.current.style.maxWidth = '';
    // @ts-ignore
    wrapperRef.current.style.maxHeight = '';

    const computedPosition = await computePosition(beaconRef.current, wrapperRef.current, {
      ...hasManualPlacement,
      middleware: [
        ...hasAutoPlacement,
        offset(20),
        shift({
          padding: 16,
          boundary: viewportContentEl ?? undefined,
        }),
        size({
          padding: 16,
          boundary: viewportContentEl ?? undefined,
          // Unlike other middleware, in which you assign styles after computePosition() has done its work,
          // size() has its own apply function to do the work during the lifecycle:
          apply({
            availableWidth,
            availableHeight,
            elements,
          }: {
            availableWidth: number;
            availableHeight: number;
            elements: Elements;
          }) {
            // Apply styles in realtime as size impacts position
            Object.assign(elements.floating.style, {
              maxWidth: `${availableWidth}px`,
              maxHeight: `${availableHeight}px`,
            });
          },
        }),
        arrow({ element: arrowRef.current }),
      ],
    });

    scrollTooltipIntoView({ isActive, selector });
    setFoatingComputedPosition(computedPosition);
  }, [tooltipPosition, viewportContentEl, isActive, selector]);

  const { middlewareData, placement } = floatingComputedPosition;

  const arrowCss = useMemo(() => {
    const { arrow: arrowComputedData } = middlewareData || {};

    if (!arrowComputedData || !wrapperRef.current) {
      return { opacity: '0' };
    }

    const { x, y } = arrowComputedData;
    if (x && x < wrapperRef.current.offsetWidth - 20) {
      const key = placement === Position.Top ? 'bottom' : 'top';
      return {
        left: x,
        [key]: -(arrowRef.current?.offsetHeight || 0) / 2,
      };
    }
    if (y && y < wrapperRef.current.offsetHeight - 20) {
      const key = placement === Position.Left ? 'right' : 'left';
      return {
        top: y,
        [key]: -(arrowRef.current?.offsetWidth || 0) / 2,
      };
    }
    return { opacity: '0' };
  }, [arrowRef.current, middlewareData, placement, wrapperRef.current]);

  const wrapperCss = useMemo(() => {
    return {
      top: floatingComputedPosition.y || 0,
      left: floatingComputedPosition.x || 0,
    };
  }, [floatingComputedPosition]);

  useEffect(() => {
    if (!wrapperRef.current && !viewportWrapperEl) {
      return () => null;
    }
    // make sure to recalculate tooltip position after it's size changes in case its position was automatically moved
    const callback = throttle(calculateSetPosition, 50);
    const observer = new ResizeObserver(callback);
    if (wrapperRef.current) {
      observer.observe(wrapperRef.current);
    }
    if (viewportWrapperEl) {
      observer.observe(viewportWrapperEl);
    }

    return () => observer.disconnect();
  }, [wrapperRef.current, viewportWrapperEl, calculateSetPosition]);

  return {
    refs: {
      wrapper: wrapperRef,
      arrow: arrowRef,
    },
    css: {
      wrapper: wrapperCss,
      arrow: arrowCss,
    },
    recalculate: () => {
      // make sure we update position after styling or placement changes and when switching between hotspots
      setFoatingComputedPosition({} as ComputePositionReturn);
      calculateSetPosition();
    },
  };
};

export const scrollTooltipIntoView = ({
  isActive,
  selector,
}: {
  isActive: boolean;
  selector?: string;
}) => {
  if (isActive) {
    const targetEl = getSelectorFromIframe(selector);
    targetEl?.scrollIntoView({ block: 'start' });
  }
};
